/* EYEMOS homepage redesign — behaviour for hp-* sections.
   Reveal-on-scroll, count-up numbers, scroll-snap carousels, sticky shop bar,
   hero video: orientation-aware source selection + autoplay guard. Vanilla JS, no dependencies, editor-safe. */
(function () {
  'use strict';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function fmt(n, dec) {
    var s = Number(n).toFixed(dec);
    if (dec === 0) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return s;
  }

  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-hp-count'));
    var dec = parseInt(el.getAttribute('data-hp-decimals') || '0', 10);
    if (isNaN(target)) return;
    if (reduce || !window.requestAnimationFrame) { el.textContent = fmt(target, dec); return; }
    var t0 = null, dur = 1400;
    function step(ts) {
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var e = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(target * e, dec);
      if (p < 1) requestAnimationFrame(step); else el.textContent = fmt(target, dec);
    }
    requestAnimationFrame(step);
  }

  function initReveal(root) {
    var els = root.querySelectorAll('[data-hp-reveal]:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var t = en.target;
        t.classList.add('is-in');
        t.querySelectorAll('[data-hp-count]').forEach(function (c) {
          if (c.getAttribute('data-hp-done')) return;
          c.setAttribute('data-hp-done', '1');
          countUp(c);
        });
        io.unobserve(t);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach(function (e) { io.observe(e); });
  }

  function initCarousels(root) {
    root.querySelectorAll('[data-hp-carousel]').forEach(function (wrap) {
      if (wrap.getAttribute('data-hp-init')) return;
      wrap.setAttribute('data-hp-init', '1');
      var track = wrap.querySelector('.hp-car');
      var prev = wrap.querySelector('[data-hp-prev]');
      var next = wrap.querySelector('[data-hp-next]');
      if (!track) return;
      function step() {
        var first = track.firstElementChild;
        if (!first) return 300;
        var cs = getComputedStyle(track);
        var gap = parseFloat(cs.columnGap || cs.gap) || 16;
        return first.getBoundingClientRect().width + gap;
      }
      function update() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  function initSticky(root) {
    var bar = root.querySelector('[data-hp-sticky]');
    if (!bar || bar.getAttribute('data-hp-init')) return;
    bar.setAttribute('data-hp-init', '1');
    var hero = document.querySelector('[data-hp-hero]');
    if (!hero || !('IntersectionObserver' in window)) return;
    var footer = document.querySelector('.footer') || document.querySelector('footer');
    var heroOut = false, footIn = false;
    function apply() {
      var on = heroOut && !footIn;
      bar.classList.toggle('is-on', on);
      var pb = document.getElementById('preview-bar-iframe');
      bar.style.setProperty('--hp-sticky-offset', pb ? pb.getBoundingClientRect().height + 'px' : '0px');
      var stt = document.querySelector('.scroll-to-top-btn');
      if (stt) stt.style.bottom = on ? (20 + bar.offsetHeight) + 'px' : '';
    }
    new IntersectionObserver(function (en) {
      heroOut = !en[0].isIntersecting && en[0].boundingClientRect.top < 0;
      apply();
    }, { threshold: 0 }).observe(hero);
    if (footer) {
      new IntersectionObserver(function (en) { footIn = en[0].isIntersecting; apply(); }, { threshold: 0 }).observe(footer);
    }
  }

  function initVideo(root) {
    root.querySelectorAll('video[data-hp-video]').forEach(function (v) {
      if (v.getAttribute('data-hp-vinit')) return;
      v.setAttribute('data-hp-vinit', '1');
      v.addEventListener('playing', function () { v.classList.add('is-playing'); });
      var srcL = v.getAttribute('data-hp-src-l') || '';
      var srcP = v.getAttribute('data-hp-src-p') || srcL;
      var conn = navigator.connection || {};
      var lite = reduce || conn.saveData === true;
      function tryPlay() {
        var p = v.play && v.play();
        if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay blocked: poster stays visible */ });
      }
      if (!srcL && !srcP) {
        /* legacy inline <source> video */
        if (reduce) { v.removeAttribute('autoplay'); v.pause && v.pause(); return; }
        tryPlay();
        return;
      }
      /* reduced motion or data-saver: keep the poster image only */
      if (lite) { v.removeAttribute('autoplay'); return; }
      var mq = window.matchMedia ? window.matchMedia('(orientation: portrait)') : null;
      function pick() {
        var src = (mq && mq.matches) ? srcP : srcL;
        if (!src) src = srcL || srcP;
        if (v.getAttribute('src') !== src) {
          v.classList.remove('is-playing');
          v.setAttribute('src', src);
          v.load();
        }
        tryPlay();
      }
      pick();
      if (mq) {
        if (mq.addEventListener) mq.addEventListener('change', pick);
        else if (mq.addListener) mq.addListener(pick);
      }
      document.addEventListener('visibilitychange', function () { if (!document.hidden && v.paused) tryPlay(); });
    });
  }

  function init(root) {
    root = root || document;
    initReveal(root);
    initCarousels(root);
    initSticky(root);
    initVideo(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
